// Conciliação GSheet × ERP em etapas (pedido do Gustavo, 20/10/2026):
//  1 — valor total do contrato: GSheet, contrato do ERP, parcelas do financeiro ou digitado;
//  2 — entregue antes do controle (até 30/09): quais parcelas, ou que parte delas,
//      já estavam ENTREGUES (produto pronto pela operação) — entregar ≠ faturar.
// Tudo só no controle de entregas: contrato e parcelas no financeiro não mudam.
import { useMemo, useState } from 'react';
import { App as AntApp, Button, Checkbox, Dropdown, Empty, Flex, InputNumber, Modal, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import * as api from './api';
import type { EtapaEntregue, EtapaSetor, EtapaValor, ParcelaEtapa2, StatusEtapa } from './api';
import { brl, dataBr, nomeDe, SETOR_CURTO, SETOR_NOMES } from './regras';

const { Text, Paragraph } = Typography;

export const TAG_STATUS: Record<StatusEtapa, { cor: string; texto: string }> = {
  ok: { cor: 'green', texto: 'ok' },
  conciliado: { cor: 'cyan', texto: 'conciliado' },
  divergente: { cor: 'orange', texto: 'divergente' },
  sem_planilha: { cor: 'default', texto: 'fora da GSheet' },
};
const ORIGEM: Record<string, string> = { contrato: 'contrato do ERP', financeiro: 'parcelas do financeiro', planilha: 'GSheet', manual: 'digitado' };
const igual = (a: number | null | undefined, b: number | null | undefined) => a != null && b != null && Math.abs(a - b) <= 1;

// ── Etapa 1 ─────────────────────────────────────────────────────────────────
export function EtapaValores({ linhas, podeEditar, onMudou }: { linhas: EtapaValor[]; podeEditar: boolean; onMudou: () => Promise<void> | void }) {
  const { message } = AntApp.useApp();
  const [soDivergentes, setSoDivergentes] = useState(true);
  const [digitando, setDigitando] = useState<EtapaValor | null>(null);
  const [valor, setValor] = useState<number | null>(null);
  const dados = linhas.filter(l => !soDivergentes || l.status === 'divergente');
  const escolher = async (l: EtapaValor, origem: Parameters<typeof api.contratoValor>[1], v?: number) => {
    try { await api.contratoValor(l.contrato_id, origem, v); message.success(`${l.numero}: valor do controle atualizado.`); await onMudou(); }
    catch (e) { message.error(api.erroDe(e)); }
  };
  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Valor total de cada contrato na GSheet × no ERP. Onde diverge, escolha qual vale <b>no controle de entregas</b> (o contrato e as parcelas no ERP não mudam).
        “Contrato do ERP” = contrato + aditivos (operacional); “parcelas do financeiro” = soma das parcelas não canceladas.
      </Paragraph>
      <Flex gap={6} align="center" style={{ marginBottom: 8 }}><Switch size="small" checked={soDivergentes} onChange={setSoDivergentes} /><Text>Só divergentes</Text></Flex>
      <Table<EtapaValor> rowKey="contrato_id" size="small" pagination={false} dataSource={dados} scroll={{ x: 1000 }}
        locale={{ emptyText: <Empty description="Nenhum contrato divergente nesta etapa." /> }}
        columns={[
          { title: 'Contrato', render: (_, l) => <span><Text strong>{l.numero}</Text> {l.titulo}<br /><Text type="secondary" style={{ fontSize: 12 }}>{nomeDe(l.responsavel)}</Text></span> },
          { title: 'GSheet', dataIndex: 'planilha', align: 'right', render: v => (v == null ? '—' : <Text strong>{brl(v, 2)}</Text>) },
          { title: 'Contrato (ERP)', dataIndex: 'contrato', align: 'right', render: (v, l) => <Text type={igual(v, l.planilha) ? undefined : 'warning'}>{brl(v, 2)}</Text> },
          { title: 'Parcelas (financeiro)', dataIndex: 'parcelas', align: 'right', render: (v, l) => <Text type={igual(v, l.planilha) ? undefined : 'warning'}>{brl(v, 2)}</Text> },
          {
            title: 'No controle', dataIndex: 'controle', align: 'right', render: (v, l) => (
              <span>{brl(v, 2)}<br /><Tag style={{ marginRight: 0 }}>{ORIGEM[l.origem] || l.origem}</Tag></span>
            ),
          },
          {
            title: 'Situação', dataIndex: 'status', render: (s: StatusEtapa, l) => (
              <Tooltip title={l.conciliado_em ? `Escolhido por ${nomeDe(l.conciliado_por)} em ${dataBr(l.conciliado_em)}` : undefined}>
                <Tag color={TAG_STATUS[s].cor}>{TAG_STATUS[s].texto}</Tag>
              </Tooltip>
            ),
          },
          {
            title: '', render: (_, l) => podeEditar && (
              <Dropdown trigger={['click']} menu={{
                items: [
                  ...(l.planilha != null ? [{ key: 'p', label: `Usar a GSheet (${brl(l.planilha, 2)})`, onClick: () => escolher(l, 'planilha') }] : []),
                  { key: 'c', label: `Usar o contrato do ERP (${brl(l.contrato, 2)})`, onClick: () => escolher(l, 'contrato') },
                  { key: 'f', label: `Usar as parcelas do financeiro (${brl(l.parcelas, 2)})`, onClick: () => escolher(l, 'financeiro') },
                  { key: 'm', label: 'Digitar o valor…', onClick: () => { setValor(l.controle); setDigitando(l); } },
                  ...(l.conciliado_em ? [{ key: 'x', label: 'Desfazer a escolha', onClick: () => escolher(l, 'limpar') }] : []),
                ],
              }}><Button size="small">Escolher ▾</Button></Dropdown>
            ),
          },
        ]} />
      {digitando && (
        <Modal open title={`Valor total no controle — ${digitando.numero}`} okText="Salvar" onCancel={() => setDigitando(null)} destroyOnHidden
          onOk={async () => { await escolher(digitando, 'manual', valor ?? undefined); setDigitando(null); }} okButtonProps={{ disabled: valor == null }}
          width="94vw" style={{ maxWidth: 460, top: 16 }}>
          <Flex vertical gap={8}>
            <Text type="secondary">GSheet {brl(digitando.planilha, 2)} · contrato {brl(digitando.contrato, 2)} · parcelas {brl(digitando.parcelas, 2)}</Text>
            <InputNumber min={0} decimalSeparator="," style={{ width: 220 }} value={valor ?? undefined} onChange={v => setValor(v)} />
          </Flex>
        </Modal>
      )}
    </div>
  );
}

// ── Etapa 3 ─────────────────────────────────────────────────────────────────
const SETORES_5 = ['projetos', 'topografia_hidrometria', 'hidrologia_meio_ambiente', 'fiscalizacao', 'seguranca_barragens'];
const pctDivisao = (r: Record<string, number> | null | undefined) =>
  Object.entries(r || {}).filter(([, v]) => v > 0.004).sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${SETOR_CURTO[k] || k} ${Math.round(v * 100)}%`).join(' · ') || '—';

export function EtapaSetores({ linhas, podeEditar, onMudou }: { linhas: EtapaSetor[]; podeEditar: boolean; onMudou: () => Promise<void> | void }) {
  const { message } = AntApp.useApp();
  const [soDivergentes, setSoDivergentes] = useState(true);
  const [soQueImpacta, setSoQueImpacta] = useState(false);
  const [editando, setEditando] = useState<EtapaSetor | null>(null);
  const [pct, setPct] = useState<Record<string, number | null>>({});
  const dados = linhas.filter(l => (!soDivergentes || l.status === 'divergente') && (!soQueImpacta || l.impacta))
    .sort((a, b) => (b.diferenca_planilha_controle ?? -1) - (a.diferenca_planilha_controle ?? -1));
  const soma = Object.values(pct).reduce<number>((a, b) => a + (b || 0), 0);
  const escolher = async (l: EtapaSetor, origem: Parameters<typeof api.contratoRateio>[1], percentuais?: Record<string, number>) => {
    try { await api.contratoRateio(l.contrato_id, origem, percentuais); message.success(`${l.numero}: divisão do controle atualizada.`); await onMudou(); }
    catch (e) { message.error(api.erroDe(e)); }
  };
  const abrirEdicao = (l: EtapaSetor) => {
    setPct(Object.fromEntries(SETORES_5.map(s => [s, l.controle?.[s] ? Math.round(l.controle[s] * 1000) / 10 : null])));
    setEditando(l);
  };
  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Divisão do contrato entre setores na GSheet × no ERP (parcelas do comercial/financeiro) × a que o controle usa nos indicadores. Escolha qual vale
        <b> no controle de entregas</b>: a divisão vale para todos os entregáveis do contrato, e as entregas já lançadas passam a usá-la. Parcelas, DRE e Receita
        por setor não mudam. “Afeta indicadores” = ainda tem saldo a entregar ou entrega lançada desde outubro (o histórico até setembro vem da planilha).
      </Paragraph>
      <Flex gap={16} align="center" wrap style={{ marginBottom: 8 }}>
        <Flex gap={6} align="center"><Switch size="small" checked={soDivergentes} onChange={setSoDivergentes} /><Text>Só divergentes</Text></Flex>
        <Flex gap={6} align="center"><Switch size="small" checked={soQueImpacta} onChange={setSoQueImpacta} /><Text>Só os que afetam indicadores</Text></Flex>
      </Flex>
      <Table<EtapaSetor> rowKey="contrato_id" size="small" pagination={false} dataSource={dados} scroll={{ x: 1100 }}
        locale={{ emptyText: <Empty description="Nenhum contrato divergente nesta etapa." /> }}
        columns={[
          { title: 'Contrato', render: (_, l) => <span><Text strong>{l.numero}</Text> {l.titulo}<br /><Text type="secondary" style={{ fontSize: 12 }}>{nomeDe(l.responsavel)}</Text></span> },
          { title: 'GSheet', dataIndex: 'planilha', render: v => <Text strong>{pctDivisao(v)}</Text> },
          { title: 'ERP', dataIndex: 'erp', render: (v, l) => <Text type={(l.diferenca_planilha_erp ?? 0) > 5 ? 'warning' : undefined}>{pctDivisao(v)}</Text> },
          {
            title: 'No controle', dataIndex: 'controle', render: (v, l) => (
              <span>{pctDivisao(v)}<br /><Tag style={{ marginRight: 0 }}>{l.origem === 'controle' ? 'escolhida no controle' : 'do ERP'}</Tag></span>
            ),
          },
          { title: 'Maior diferença', dataIndex: 'diferenca_planilha_controle', align: 'right', render: v => (v == null ? '—' : v <= 5 ? <Text type="success">{v} p.p.</Text> : <Text type="danger">{v} p.p.</Text>) },
          {
            title: 'Afeta indicadores', align: 'right', render: (_, l) => l.impacta
              ? <span>{l.saldo_a_entregar > 0 ? `saldo ${brl(l.saldo_a_entregar)}` : ''}{l.saldo_a_entregar > 0 && l.entregue_no_controle > 0 ? <br /> : null}{l.entregue_no_controle > 0 ? `entregue ${brl(l.entregue_no_controle)}` : ''}</span>
              : <Text type="secondary">não</Text>,
          },
          {
            title: 'Situação', dataIndex: 'status', render: (s: StatusEtapa, l) => (
              <Tooltip title={l.conciliado_em ? `Escolhido por ${nomeDe(l.conciliado_por)} em ${dataBr(l.conciliado_em)}` : undefined}>
                <Tag color={TAG_STATUS[s].cor}>{TAG_STATUS[s].texto}</Tag>
              </Tooltip>
            ),
          },
          {
            title: '', render: (_, l) => podeEditar && (
              <Dropdown trigger={['click']} menu={{
                items: [
                  ...(l.planilha ? [{ key: 'p', label: `Usar a GSheet (${pctDivisao(l.planilha)})`, onClick: () => escolher(l, 'planilha') }] : []),
                  { key: 'e', label: `Usar o ERP (${pctDivisao(l.erp)})`, onClick: () => escolher(l, 'financeiro') },
                  { key: 'm', label: 'Editar %…', onClick: () => abrirEdicao(l) },
                  ...(l.conciliado_em || l.origem === 'controle' ? [{ key: 'x', label: 'Desfazer a escolha (volta ao ERP)', onClick: () => escolher(l, 'limpar') }] : []),
                ],
              }}><Button size="small">Escolher ▾</Button></Dropdown>
            ),
          },
        ]} />
      {editando && (
        <Modal open title={`Divisão por setor no controle — ${editando.numero}`} okText="Salvar" onCancel={() => setEditando(null)} destroyOnHidden
          okButtonProps={{ disabled: Math.abs(soma - 100) > 0.5 }} width="94vw" style={{ maxWidth: 560, top: 16 }}
          onOk={async () => {
            const percentuais = Object.fromEntries(Object.entries(pct).filter(([, v]) => v).map(([k, v]) => [k, v as number]));
            await escolher(editando, 'manual', percentuais); setEditando(null);
          }}>
          <Flex vertical gap={8}>
            <Text type="secondary">GSheet: {pctDivisao(editando.planilha)} · ERP: {pctDivisao(editando.erp)}</Text>
            {SETORES_5.map(s => (
              <Flex key={s} justify="space-between" align="center" gap={8}>
                <Text>{SETOR_NOMES[s]}</Text>
                <InputNumber min={0} max={100} step={5} decimalSeparator="," style={{ width: 120 }} value={pct[s] ?? undefined} onChange={v => setPct(p => ({ ...p, [s]: v }))} />
              </Flex>
            ))}
            <Text type={Math.abs(soma - 100) > 0.5 ? 'danger' : 'success'}>Soma: {soma.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%</Text>
          </Flex>
        </Modal>
      )}
    </div>
  );
}

// ── Etapa 2 ─────────────────────────────────────────────────────────────────
export function EtapaEntregueAntes({ linhas, corte, podeEditar, onMudou }: {
  linhas: EtapaEntregue[]; corte: string; podeEditar: boolean; onMudou: () => Promise<void> | void;
}) {
  const [soDivergentes, setSoDivergentes] = useState(true);
  const dados = linhas.filter(l => !soDivergentes || l.status === 'divergente');
  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        “Entregue” aqui é o <b>produto finalizado pela operação, em condição de pleitear o faturamento</b> — não é a nota. Para cada contrato, compare o
        “Entregue acumulado” da GSheet com o faturado até {dataBr(corte)}. Se batem, está ok. Se não, abra o contrato e marque quais parcelas (ou que parte delas)
        já estavam entregues até {dataBr(corte)}: vale marcar parcela ainda não faturada e desmarcar parcela faturada antecipadamente. Ao confirmar, o acumulado do
        contrato no controle passa a ser exatamente a soma marcada. De outubro em diante, as entregas são lançadas na aba Mês.
      </Paragraph>
      <Flex gap={6} align="center" style={{ marginBottom: 8 }}><Switch size="small" checked={soDivergentes} onChange={setSoDivergentes} /><Text>Só divergentes</Text></Flex>
      <Table<EtapaEntregue> rowKey="contrato_id" size="small" pagination={false} dataSource={dados} scroll={{ x: 1000 }}
        locale={{ emptyText: <Empty description="Nenhum contrato divergente nesta etapa." /> }}
        expandable={{ expandedRowRender: l => <EditorEntregueAntes linha={l} corte={corte} podeEditar={podeEditar} onMudou={onMudou} /> }}
        columns={[
          { title: 'Contrato', render: (_, l) => <span><Text strong>{l.numero}</Text> {l.titulo}<br /><Text type="secondary" style={{ fontSize: 12 }}>{nomeDe(l.responsavel)}</Text></span> },
          { title: `GSheet: entregue até ${dataBr(corte)}`, dataIndex: 'planilha', align: 'right', render: v => (v == null ? '—' : <Text strong>{brl(v, 2)}</Text>) },
          {
            title: `Faturado até ${dataBr(corte)}`, dataIndex: 'faturado_ate_corte', align: 'right', render: (v, l) => (
              <span>{brl(v, 2)}{l.planilha != null && <><br /><Tag color={l.faturado_bate ? 'green' : 'orange'} style={{ marginRight: 0 }}>{l.faturado_bate ? 'bate com a GSheet' : 'difere da GSheet'}</Tag></>}</span>
            ),
          },
          { title: 'Marcado como entregue no controle', dataIndex: 'marcado', align: 'right', render: v => brl(v, 2) },
          { title: 'Falta marcar', dataIndex: 'diferenca', align: 'right', render: v => (v == null ? '—' : Math.abs(v) <= 1 ? <Text type="success">—</Text> : <Text type="danger">{brl(v, 2)}</Text>) },
          {
            title: 'Situação', dataIndex: 'status', render: (s: StatusEtapa, l) => (
              <Tooltip title={l.conciliado_em ? `Confirmado por ${nomeDe(l.conciliado_por)} em ${dataBr(l.conciliado_em)}` : (Math.abs(l.abertura - l.marcado) > 1 ? `Acumulado gravado hoje: ${brl(l.abertura, 2)}` : undefined)}>
                <Tag color={TAG_STATUS[s].cor}>{TAG_STATUS[s].texto}</Tag>
              </Tooltip>
            ),
          },
        ]} />
    </div>
  );
}

function EditorEntregueAntes({ linha, corte, podeEditar, onMudou }: { linha: EtapaEntregue; corte: string; podeEditar: boolean; onMudou: () => Promise<void> | void }) {
  const { message, modal } = AntApp.useApp();
  const [valores, setValores] = useState<Record<string, number>>(() => Object.fromEntries(linha.itens.map(i => [i.id, i.entregue_antes])));
  const [salvando, setSalvando] = useState(false);
  const teto = (i: ParcelaEtapa2) => Math.max(0, Math.round((i.valor - i.entregue_no_controle) * 100) / 100);
  const soma = useMemo(() => Math.round(Object.values(valores).reduce((s, v) => s + (v || 0), 0) * 100) / 100, [valores]);
  const alvo = linha.planilha;
  const falta = alvo == null ? null : Math.round((alvo - soma) * 100) / 100;
  const set = (id: string, v: number) => setValores(p => ({ ...p, [id]: v }));

  async function salvar() {
    const mudados = linha.itens.filter(i => Math.abs((valores[i.id] || 0) - i.entregue_antes) > 0.004);
    const enviar = async () => {
      setSalvando(true);
      try {
        await api.contratoEntregueAntes(linha.contrato_id, mudados.map(i => ({ id: i.id, versao: i.versao, valor_antes: valores[i.id] || 0 })), true);
        message.success(`${linha.numero}: entregue até ${dataBr(corte)} confirmado em ${brl(soma, 2)}.`);
        await onMudou();
      } catch (e) { message.error(api.erroDe(e)); }
      finally { setSalvando(false); }
    };
    if (falta != null && Math.abs(falta) > 1) {
      modal.confirm({
        title: 'A soma marcada não bate com a GSheet',
        content: `Marcado ${brl(soma, 2)} × GSheet ${brl(alvo, 2)} (diferença ${brl(falta, 2)}). Confirmar assim mesmo? O acumulado do contrato no controle passa a ser ${brl(soma, 2)}.`,
        okText: 'Confirmar assim mesmo', cancelText: 'Voltar', onOk: enviar,
      });
    } else await enviar();
  }

  return (
    <div style={{ padding: '4px 0' }}>
      <Table<ParcelaEtapa2> rowKey="id" size="small" pagination={false} dataSource={linha.itens} scroll={{ x: 900 }}
        columns={[
          { title: 'Parcela / entregável', render: (_, i) => <span>{i.numero_parcela != null && <Text type="secondary">{i.numero_parcela}{i.total_parcelas ? `/${i.total_parcelas}` : ''} · </Text>}{i.descricao}</span> },
          { title: 'Valor', dataIndex: 'valor', align: 'right', render: v => brl(v, 2) },
          {
            title: 'Financeiro', render: (_, i) => !i.parcela_id ? <Tag>sem parcela</Tag>
              : i.faturada_ate_corte ? <Tag color="blue">faturada {dataBr(i.parcela_faturada_em)}{i.parcela_nf ? ` · NF ${i.parcela_nf}` : ''}</Tag>
                : i.parcela_nf || ['faturada', 'recebida', 'parcialmente_recebida'].includes(i.parcela_status || '')
                  ? <Tag>faturada depois de {dataBr(corte)}</Tag> : <Tag>em aberto</Tag>,
          },
          {
            title: `Entregue até ${dataBr(corte)}`, width: 300, render: (_, i) => {
              const v = valores[i.id] || 0;
              const max = teto(i);
              return (
                <Flex gap={8} align="center">
                  <Checkbox disabled={!podeEditar || max <= 0} checked={max > 0 && Math.abs(v - max) < 0.005} indeterminate={v > 0.004 && Math.abs(v - max) >= 0.005}
                    onChange={e => set(i.id, e.target.checked ? max : 0)}>inteira</Checkbox>
                  <InputNumber size="small" min={0} max={max} decimalSeparator="," disabled={!podeEditar || max <= 0} style={{ width: 130 }}
                    value={v} onChange={x => set(i.id, Math.min(max, Math.max(0, Number(x) || 0)))} />
                </Flex>
              );
            },
          },
          { title: 'Já entregue no controle (out+)', dataIndex: 'entregue_no_controle', align: 'right', render: v => (v ? brl(v, 2) : '—') },
        ]} />
      <Flex gap={12} align="center" wrap style={{ marginTop: 8 }}>
        <Text>Marcado: <b>{brl(soma, 2)}</b></Text>
        <Text>GSheet: <b>{brl(alvo, 2)}</b></Text>
        {falta != null && <Text type={Math.abs(falta) <= 1 ? 'success' : 'danger'}>{Math.abs(falta) <= 1 ? 'bate com a GSheet' : `falta ${brl(falta, 2)}`}</Text>}
        <div style={{ flex: 1 }} />
        {podeEditar && <>
          <Button size="small" onClick={() => setValores(Object.fromEntries(linha.itens.map(i => [i.id, i.faturada_ate_corte ? teto(i) : 0])))}>Marcar as faturadas até {dataBr(corte)}</Button>
          <Button size="small" onClick={() => setValores(Object.fromEntries(linha.itens.map(i => [i.id, 0])))}>Limpar</Button>
          <Button size="small" type="primary" loading={salvando} onClick={salvar}>Confirmar entregue = {brl(soma, 2)}</Button>
        </>}
      </Flex>
    </div>
  );
}
